/**
 * EmpAI cart pack consolidator.
 * Builds sheet totals from real pack / single SKUs only — never Shopify discount codes.
 * Odd counts use min-cost pack combinations (e.g. 21 → 2×Set of 10 + 1 single).
 */
(function () {
  if (!/\/cart\/?$/.test(window.location.pathname)) return;

  var SET_TIERS = [10, 7, 5, 3, 2, 1];

  function packFromHandle(handle) {
    var m = String(handle || "").match(/-pack-(\d+)$/i);
    return m ? Number(m[1]) : 1;
  }

  function baseHandle(handle) {
    return String(handle || "").replace(/-pack-\d+$/i, "");
  }

  function fetchProduct(handle) {
    return fetch("/products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
    }).then(function (r) {
      if (!r.ok) throw new Error("missing " + handle);
      return r.json();
    });
  }

  /**
   * Min-cost pack plan for N sheets using only catalog pack/single SKUs.
   * Does NOT invent unit rates or create discount codes.
   */
  function resolveVolumePricing(sheetCount, packs) {
    var n = Math.max(0, Math.floor(Number(sheetCount) || 0));
    if (!n || !packs.length) return null;

    var byQty = {};
    packs.forEach(function (p) {
      var q = Math.max(1, Math.floor(Number(p.qty) || 1));
      var price = Math.round(Number(p.priceCents) || 0);
      if (!price) return;
      if (!byQty[q] || price < byQty[q].priceCents) {
        byQty[q] = {
          qty: q,
          variantId: p.variantId,
          handle: p.handle,
          priceCents: price,
        };
      }
    });

    var options = Object.keys(byQty)
      .map(Number)
      .sort(function (a, b) {
        return b - a;
      })
      .map(function (q) {
        return byQty[q];
      });
    if (!options.length) return null;
    if (!byQty[1]) return null;

    function line(pack, quantity) {
      return {
        id: pack.variantId,
        quantity: quantity,
        qty: pack.qty,
        handle: pack.handle,
        priceCents: pack.priceCents,
      };
    }

    var INF = Number.POSITIVE_INFINITY;
    var cost = new Array(n + 1);
    var choice = new Array(n + 1);
    cost[0] = 0;
    choice[0] = null;
    for (var i = 1; i <= n; i++) {
      cost[i] = INF;
      choice[i] = null;
      for (var oi = 0; oi < options.length; oi++) {
        var pack = options[oi];
        if (pack.qty > i) continue;
        var prev = cost[i - pack.qty];
        if (prev === INF) continue;
        var next = prev + pack.priceCents;
        if (next < cost[i]) {
          cost[i] = next;
          choice[i] = pack;
        }
      }
    }

    if (cost[n] === INF) {
      var singleOnly = byQty[1];
      return {
        mode: "singles",
        items: [line(singleOnly, n)],
        sheetCount: n,
        unitCents: singleOnly.priceCents,
        targetCents: singleOnly.priceCents * n,
        discountCents: 0,
        basisQty: 1,
      };
    }

    var counts = {};
    var left = n;
    while (left > 0 && choice[left]) {
      var pick = choice[left];
      counts[pick.qty] = (counts[pick.qty] || 0) + 1;
      left -= pick.qty;
    }
    if (left !== 0) {
      var fallback = byQty[1];
      return {
        mode: "singles",
        items: [line(fallback, n)],
        sheetCount: n,
        unitCents: fallback.priceCents,
        targetCents: fallback.priceCents * n,
        discountCents: 0,
        basisQty: 1,
      };
    }

    var items = Object.keys(counts)
      .map(Number)
      .sort(function (a, b) {
        return b - a;
      })
      .map(function (q) {
        return line(byQty[q], counts[q]);
      });

    var largest = items[0] ? items[0].qty : 1;
    var mode = items.length === 1 && items[0].qty === n ? "exact" : "packs";
    return {
      mode: mode,
      items: items,
      sheetCount: n,
      unitCents: n > 0 ? Math.round(cost[n] / n) : 0,
      targetCents: cost[n],
      discountCents: 0,
      basisQty: largest,
    };
  }

  function samePlan(currentLines, planItems) {
    if (currentLines.length !== planItems.length) return false;
    var a = currentLines
      .map(function (l) {
        return l.id + ":" + l.quantity;
      })
      .sort()
      .join("|");
    var b = planItems
      .map(function (l) {
        return l.id + ":" + l.quantity;
      })
      .sort()
      .join("|");
    return a === b;
  }

  function loadPacksForBase(base) {
    return Promise.all(
      SET_TIERS.map(function (qty) {
        var handle = qty <= 1 ? base : base + "-pack-" + qty;
        return fetchProduct(handle)
          .then(function (json) {
            return {
              qty: qty,
              handle: handle,
              variantId: Number(json.variants[0].id),
              priceCents: Math.round(Number(json.price)),
            };
          })
          .catch(function () {
            return null;
          });
      }),
    ).then(function (rows) {
      return rows.filter(Boolean);
    });
  }

  function consolidate() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var groups = {};
        (cart.items || []).forEach(function (item) {
          if (item.properties && Object.keys(item.properties).length) return;
          if (/custom\s*cut/i.test(item.product_title || "")) return;
          var handle = item.handle;
          if (!handle) return;
          var base = baseHandle(handle);
          var sheets = packFromHandle(handle) * (item.quantity || 0);
          if (!groups[base]) {
            groups[base] = { base: base, sheets: 0, keys: [], lines: [] };
          }
          groups[base].sheets += sheets;
          groups[base].keys.push(item.key);
          groups[base].lines.push({
            id: item.id,
            quantity: item.quantity,
            handle: handle,
          });
        });

        var bases = Object.keys(groups).filter(function (b) {
          return groups[b].sheets >= 2;
        });
        if (!bases.length) return null;

        return Promise.all(
          bases.map(function (base) {
            return loadPacksForBase(base).then(function (packs) {
              var plan = resolveVolumePricing(groups[base].sheets, packs);
              if (!plan || !plan.items.length) return null;
              if (samePlan(groups[base].lines, plan.items)) return null;
              return { group: groups[base], plan: plan };
            });
          }),
        ).then(function (jobs) {
          return jobs.filter(Boolean);
        });
      })
      .then(function (jobs) {
        if (!jobs || !jobs.length) return false;

        var updates = {};
        jobs.forEach(function (job) {
          job.group.keys.forEach(function (key) {
            updates[key] = 0;
          });
        });

        return fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({ updates: updates }),
        })
          .then(function (r) {
            return r.json();
          })
          .then(function () {
            var items = [];
            jobs.forEach(function (job) {
              job.plan.items.forEach(function (line) {
                items.push({ id: line.id, quantity: line.quantity });
              });
            });
            return fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              credentials: "same-origin",
              body: JSON.stringify({ items: items }),
            }).then(function () {
              return true;
            });
          })
          .then(function () {
            window.location.reload();
            return true;
          });
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      consolidate().catch(function () {});
    });
  } else {
    consolidate().catch(function () {});
  }
})();
